import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Box, Text, Checkbox, HStack, Button, Spinner, Center, useToast,
} from '@chakra-ui/react';
import { Form, SelectField } from '../../components/form';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';

const SMS_CHANNEL_ID = 1;

/**
 * ChannelConfigModal — pick which connected account is the active route for a channel.
 *   • SMS: the Admin Portal's rich routing — OTP & Transaction and Marketing, each able
 *     to use one account or a separate Local / International pair.
 *   • Every other channel: a single "Active Account" dropdown to switch between the
 *     accounts connected for that channel.
 * Persists the SAME `channel_config` shape the Admin Portal writes, via the tenant-scoped
 * /channel_api_config/my-channel-config (customer_id pinned from token). Entries for other
 * channels are preserved untouched.
 */
export default function ChannelConfigModal({ isOpen, onClose, channel, accounts, onSaved }) {
  const toast = useToast();
  const channelId = channel?.channelId;
  const isSms = Number(channelId) === SMS_CHANNEL_ID;
  // SelectField wants { data: [{ id, name }] }; ids are strings to match option values.
  const accountList = { data: (accounts || []).map((a) => ({ id: String(a.id), name: a.name })) };

  const [loading, setLoading] = useState(true);
  const [otherConfig, setOtherConfig] = useState([]); // entries for OTHER channels — preserved
  const [otpSplit, setOtpSplit] = useState(false);
  const [mkSplit, setMkSplit] = useState(false);
  const [initialValues, setInitialValues] = useState({
    primary: '', primary_local: '', primary_international: '',
    mk_primary: '', mk_primary_local: '', mk_primary_international: '',
    active: '',
  });

  // On open, load the current channel_config to prefill this channel + keep the rest.
  useEffect(() => {
    if (!isOpen || !channelId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await API.get(`${process.env.REACT_APP_API_URL}/channel_api_config/my-channel-config`, { headers: getApiHeaders() });
        const cc = r.data?.data?.channel_config || [];
        if (cancelled) return;
        setOtherConfig(cc.filter((c) => Number(c.channel_id) !== Number(channelId)));

        if (isSms) {
          const otp = cc.find((c) => Number(c.channel_id) === SMS_CHANNEL_ID && c.type === 'otp_trans')
            || cc.find((c) => Number(c.channel_id) === SMS_CHANNEL_ID);
          const mk = cc.find((c) => Number(c.channel_id) === SMS_CHANNEL_ID && c.type === 'marketing');
          setOtpSplit(!!otp?.is_split);
          setMkSplit(!!mk?.is_split);
          setInitialValues({
            primary: otp && !otp.is_split ? String(otp.value || '') : '',
            primary_local: otp?.is_split ? String(otp.local || '') : '',
            primary_international: otp?.is_split ? String(otp.int || '') : '',
            mk_primary: mk && !mk.is_split ? String(mk.value || '') : '',
            mk_primary_local: mk?.is_split ? String(mk.local || '') : '',
            mk_primary_international: mk?.is_split ? String(mk.int || '') : '',
            active: '',
          });
        } else {
          const entry = cc.find((c) => Number(c.channel_id) === Number(channelId));
          setInitialValues((v) => ({ ...v, active: String(entry?.value || '') }));
        }
      } catch (e) {
        if (!cancelled) setOtherConfig([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, channelId, isSms]);

  const onSubmit = async (values) => {
    let entries;
    if (isSms) {
      const otp = { channel_id: SMS_CHANNEL_ID, key: true, value: '', is_primary: true, type: 'otp_trans' };
      if (otpSplit) {
        otp.value = values.primary_local || ''; otp.local = values.primary_local || ''; otp.int = values.primary_international || ''; otp.is_split = true;
      } else {
        otp.value = values.primary || ''; otp.local = values.primary || ''; otp.int = values.primary || ''; otp.is_split = false;
      }
      const mk = { channel_id: SMS_CHANNEL_ID, key: true, value: '', is_primary: true, type: 'marketing' };
      if (mkSplit) {
        mk.value = values.mk_primary_local || ''; mk.local = values.mk_primary_local || ''; mk.int = values.mk_primary_international || ''; mk.is_split = true;
      } else {
        mk.value = values.mk_primary || ''; mk.local = values.mk_primary || ''; mk.int = values.mk_primary || ''; mk.is_split = false;
      }
      entries = [otp, mk];
    } else {
      entries = values.active ? [{ channel_id: Number(channelId), key: true, value: values.active }] : [];
    }
    const channel_config = [...otherConfig, ...entries];
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/channel_api_config/my-channel-config`, { channel_config }, { headers: getApiHeaders() });
      toast({ description: `${channel?.name} configuration saved`, status: 'success', duration: 4000, isClosable: true });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast({ description: e.message || 'Could not save configuration', status: 'error', duration: 5000, isClosable: true });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{channel?.name} Configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loading ? (
            <Center py={10}><Spinner color="blue.500" /></Center>
          ) : accountList.data.length === 0 ? (
            <Box border="1px dashed" borderColor="gray.300" borderRadius="lg" py={8} textAlign="center">
              <Text fontSize="sm" color="gray.500" fontWeight="500">No accounts connected for this channel</Text>
              <Text fontSize="xs" color="gray.400" mt={1}>Connect a provider first, then configure routing here.</Text>
            </Box>
          ) : isSms ? (
            <>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Choose which connected account sends each SMS type. Enable "Separate Local &amp; International"
                to route domestic and international traffic through different accounts.
              </Text>
              <Form enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
                <Box mb={5}>
                  <Text fontWeight="bold" mb={2}>OTP &amp; Transaction</Text>
                  <Checkbox isChecked={otpSplit} onChange={(e) => setOtpSplit(e.target.checked)} mb={2}>
                    Separate Local &amp; International Accounts
                  </Checkbox>
                  {otpSplit ? (
                    <HStack spacing={4} align="start">
                      <Box flex={1}><SelectField name="primary_local" list={accountList} title="Local Account" /></Box>
                      <Box flex={1}><SelectField name="primary_international" list={accountList} title="International Account" /></Box>
                    </HStack>
                  ) : (
                    <SelectField name="primary" list={accountList} title="Account" />
                  )}
                </Box>
                <Box mb={5}>
                  <Text fontWeight="bold" mb={2}>Marketing</Text>
                  <Checkbox isChecked={mkSplit} onChange={(e) => setMkSplit(e.target.checked)} mb={2}>
                    Separate Local &amp; International Accounts
                  </Checkbox>
                  {mkSplit ? (
                    <HStack spacing={4} align="start">
                      <Box flex={1}><SelectField name="mk_primary_local" list={accountList} title="Local Account" /></Box>
                      <Box flex={1}><SelectField name="mk_primary_international" list={accountList} title="International Account" /></Box>
                    </HStack>
                  ) : (
                    <SelectField name="mk_primary" list={accountList} title="Account" />
                  )}
                </Box>
                <Button type="submit" w="full" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}>
                  Save Configuration
                </Button>
              </Form>
            </>
          ) : (
            <>
              <Text fontSize="sm" color="gray.500" mb={4}>
                You have more than one {channel?.name} account connected — pick which one is active for sending.
              </Text>
              <Form enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
                <Box mb={5}>
                  <SelectField name="active" list={accountList} title="Active Account" />
                </Box>
                <Button type="submit" w="full" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}>
                  Save Configuration
                </Button>
              </Form>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
